import { Spline, SquareTerminal, X } from '@orgus/ui/icons';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import type { RunStatus } from '@orgus/protocol';
import { Button, Drawer, GROUP_INDENT, Gauge, GroupChevron, Pressable, RelativeTime, RowStatus, StatusPill, cn, formatCompact, formatDuration, plural, runRowStatus, shortPath, useNow } from '@orgus/ui';
import type { PillStatus } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { attemptNote, phaseGroups, workflowProgress } from './model.ts';
import type { TimelineRun } from './model.ts';
import { activeMs, noActivityReason, summarize, usableActivity } from './activity.ts';
import { ActivityLog, ActivityUnreported } from './ActivityLog.tsx';
import { burstsText } from './ActivityBar.tsx';
import { AttemptMark } from './Timeline.tsx';
import { PresenceAvatars } from '../teams/PresenceAvatars.tsx';
import { plainText } from '../../lib/plainText.ts';
import { DetailSection, FactGrid } from '../detail/FactGrid.tsx';
import type { Fact } from '../detail/FactGrid.tsx';

const PILL: Record<RunStatus, { status: PillStatus; label: string }> = {
  running: { status: 'working', label: 'Running' },
  done: { status: 'ended', label: 'Done' },
  failed: { status: 'failed', label: 'Failed' },
  stopped: { status: 'ended', label: 'Stopped' },
};

/** Whole minutes once past a minute, the same precision on every row: "48m", never "48m 09s" beside "41m". */
function minutes(ms: number): string {
  return formatDuration(ms < 60_000 ? ms : Math.floor(ms / 60_000) * 60_000);
}

/** The agent list's numeric columns, shared by its header and its rows so they line up. */
const TIME_COL = 'w-14 shrink-0 text-right text-caption text-fg-subtle num';
const TOOLS_COL = 'w-12 shrink-0 text-right text-caption text-fg-subtle num';

const sentence = (s: string) => `${s.charAt(0).toUpperCase()}${s.slice(1)}`;

/** One agent of a workflow as a one-line row; it opens into its folder, activity and last message. */
function AgentItem({ agent, now, focused }: { agent: TimelineRun; now: number; focused: boolean }) {
  const ref = useRef<HTMLLIElement>(null);
  const [open, setOpen] = useState(focused);
  useEffect(() => {
    if (focused) {
      setOpen(true);
      ref.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [focused]);
  const note = attemptNote(agent);
  return (
    <li ref={ref} className={cn(focused && 'bg-accent-soft')} aria-current={focused || undefined}>
      <Pressable data-row="" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="flex h-row items-center gap-2 px-3 hover:bg-hover">
        <GroupChevron open={open} />
        <RowStatus {...runRowStatus(agent.status)} hideLabel focusable={false} />
        <span className="min-w-0 truncate text-ui text-fg">{agent.label}</span>
        {note && <AttemptMark note={note} />}
        <span data-meta="" className={cn(TIME_COL, 'ml-auto')}>
          {minutes((agent.endedAt ?? now) - agent.startedAt)}
        </span>
        <span data-meta="" className={TOOLS_COL} title={plural(agent.toolCalls ?? 0, 'tool call')}>
          {formatCompact(agent.toolCalls ?? 0)}
        </span>
      </Pressable>
      {open && (
        // What opens sits one group step in, under the agent's status icon (GROUP_INDENT).
        <div className="px-3 pb-3">
          <div className={cn('flex flex-col gap-3', GROUP_INDENT)}>
            <FactGrid
              label={`${agent.label} facts`}
              lines={[
                [
                  { label: 'Folder', value: agent.worktree ? shortPath(agent.worktree, 40) : null, title: agent.worktree ?? undefined, mono: true },
                  { label: 'Attempt', value: note ? sentence(note) : null },
                ],
              ]}
            />
            <ActivityLog run={agent} now={now} unreported="none" />
            {agent.lastText && (
              <div className="flex min-w-0 flex-col gap-1">
                <span className="text-caption text-fg-subtle">{agent.status === 'running' ? 'Latest' : 'Last message'}</span>
                <p className="rounded-card bg-sunken px-3 py-2 text-ui text-fg-muted">{plainText(agent.lastText)}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

/** A workflow's agents grouped by phase: the phase is the group header, at row height. */
function AgentList({ run, now, focusId, machineId }: { run: TimelineRun; now: number; focusId: string | null; machineId: string | null }) {
  const groups = phaseGroups(run);
  const total = run.agents?.length ?? 0;
  // Agents whose machine reports no activity: said once, under the list, never once per agent.
  const unreported = (run.agents ?? []).some((a) => !a.activity && noActivityReason(a, now) === 'unreported');
  return (
    <DetailSection title="Agents" aside={`${plural(total + run.agentsOmitted, 'agent')}${run.agentsOmitted > 0 ? `, ${formatCompact(run.agentsOmitted)} not listed` : ''}`}>
      <div className="overflow-hidden rounded-card border border-border" data-list="">
        {/* The columns named once: each row ends in its time and its tool calls. */}
        <div className="flex h-control-sm items-center gap-2 border-b border-border-subtle px-3 text-caption text-fg-subtle" aria-hidden>
          <span className="min-w-0 flex-1">Agent</span>
          <span className={TIME_COL}>Time</span>
          <span className={TOOLS_COL}>Tools</span>
        </div>
        {groups.map((g) => {
          const stopped = g.agents.length - g.running - g.done - g.failed;
          // Failures lead, as on the timeline.
          const facts = [g.failed ? `${g.failed} failed` : null, g.running ? `${g.running} running` : null, g.done ? `${g.done} done` : null, stopped ? `${stopped} stopped` : null].filter(Boolean).join(', ');
          return (
            <section key={g.title || '(no phase)'} aria-label={g.title || 'No phase'}>
              <div data-group-header="" className="flex h-row items-center gap-3 border-b border-border-subtle bg-raised px-3 text-caption">
                <span className="font-medium text-fg-muted">{g.title || 'No phase'}</span>
                <span className="truncate text-fg-subtle num">{facts}</span>
              </div>
              <ol className="divide-y divide-border-subtle border-b border-border-subtle">
                {g.agents.map((a) => (
                  <AgentItem key={a.id} agent={a} now={now} focused={a.id === focusId} />
                ))}
              </ol>
            </section>
          );
        })}
      </div>
      {unreported && <ActivityUnreported machineId={machineId} text="Activity not reported by its machine" />}
    </DetailSection>
  );
}

/**
 * Facts in lines of two, so a phone's two columns never leave a cell on its own. An odd count
 * gives the first fact (the session's title, the longest value) a line of its own instead.
 */
export function pairs(facts: (Fact | null)[]): Fact[][] {
  const shown = facts.filter((f): f is Fact => f != null && f.value != null && f.value !== '');
  const out: Fact[][] = [];
  const head = shown.length % 2 ? 1 : 0;
  if (head) out.push(shown.slice(0, 1));
  for (let i = head; i < shown.length; i += 2) out.push(shown.slice(i, i + 2));
  return out;
}

/**
 * A secondary section folded at rest. Its chevron sits inside the content column, at the left
 * edge every row of the drawer starts from, never out in the panel's gutter.
 */
function Fold({ title, aside, children }: { title: string; aside?: string | null; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <section aria-label={title} className="flex min-w-0 flex-col gap-2">
      <Pressable aria-expanded={open} onClick={() => setOpen((v) => !v)} className="flex h-control-sm min-w-0 items-center gap-2 rounded-control text-caption hover:text-fg">
        <GroupChevron open={open} />
        <h3 className="shrink-0 font-medium text-fg-muted">{title}</h3>
        {aside && <span className="min-w-0 truncate text-fg-subtle num">{aside}</span>}
      </Pressable>
      {open && children}
    </section>
  );
}

/** The detail of one subagent or workflow run, live while it runs. `focusAgentId` opens one agent of a workflow. */
export function RunDrawer({
  run,
  onClose,
  focusAgentId = null,
  onCloseAutoFocus,
}: {
  run: TimelineRun | null;
  onClose: () => void;
  focusAgentId?: string | null;
  /** Where focus goes when the drawer closes (preventDefault to place it yourself). */
  onCloseAutoFocus?: (e: Event) => void;
}) {
  const now = useNow(1000);
  const navigate = useNavigate();
  const session = useOrgus((s) => (run ? s.sessions[run.sessionId] : undefined));
  const machine = useOrgus((s) => (session ? s.machines[session.machineId] : undefined));
  const project = useOrgus((s) => (session?.projectId ? s.projects[session.projectId] : undefined));
  const r = run;
  const flow = r?.kind === 'workflow';
  const sum = r?.agents?.length ? summarize(r.agents, now) : null;
  const kindText = flow ? 'Workflow' : r?.kind === 'agent' ? 'Workflow agent' : 'Subagent';
  const total = sum ? sum.total + (r?.agentsOmitted ?? 0) : r?.phases ? r.phases.reduce((n, p) => n + p.agents, 0) : 0;
  const finished = sum ? sum.finished : r?.phases ? r.phases.reduce((n, p) => n + p.done, 0) : 0;
  const failed = sum?.failed ?? 0;
  const active = r && !sum && usableActivity(r) ? formatDuration(activeMs(r, now).ms) : null;

  // At rest four facts: Project, Started, Running for, and Finished (n of m, with the failed
  // count) while agents are left or some failed. A finished workflow with all done says it in its
  // pill, and its tool calls take the cell.
  const progress: Fact | null =
    flow && total > 0 && (finished < total || failed > 0)
      ? {
          label: 'Finished',
          value: (
            <>
              <span className="min-w-0 truncate">{`${formatCompact(finished)} of ${formatCompact(total)}`}</span>
              {/* The failed count as an icon plus its number, so the cell never truncates it. */}
              {failed > 0 && (
                <span className="inline-flex shrink-0 items-center gap-1 text-status-danger" title={`${formatCompact(failed)} failed`}>
                  <X className="size-3" aria-hidden />
                  {formatCompact(failed)}
                  <span className="sr-only"> failed</span>
                </span>
              )}
            </>
          ),
        }
      : null;
  const tools: Fact | null = r?.toolCalls != null ? { label: 'Tool calls', value: formatCompact(r.toolCalls) } : null;
  const activeFact: Fact | null = active ? { label: 'Active', value: active } : null;
  const fourth = progress ?? tools ?? activeFact;
  const atRest: Fact[] = r
    ? ([
        { label: 'Project', value: session ? (project?.name ?? 'No project') : null },
        { label: 'Started', value: <RelativeTime ts={r.startedAt} /> },
        { label: r.endedAt ? 'Took' : 'Running for', value: formatDuration((r.endedAt ?? now) - r.startedAt) },
        fourth,
      ].filter((f) => f != null) as Fact[])
    : [];
  // The rest, folded under Details. The agent list already shows each agent's time, so the
  // longest one and the running count are not repeated here.
  const details = r
    ? pairs([
        session ? { label: 'Session', value: session.title, title: session.title } : null,
        r.kind === 'subagent' ? { label: 'Agent type', value: r.label } : null,
        session ? { label: 'Machine', value: machine?.name ?? 'Unknown machine' } : null,
        session ? { label: 'Folder', value: shortPath(session.cwd, 40), title: session.cwd, mono: true } : null,
        fourth === tools ? null : tools,
        fourth === activeFact ? null : activeFact,
      ])
    : [];

  const last = r?.lastText ? (
    <DetailSection title={r.status === 'running' ? 'Latest' : 'Last message'} aside={flow ? burstsText(r, now) : undefined}>
      <p className="max-w-prose rounded-card bg-sunken px-4 py-3 text-body text-fg">{plainText(r.lastText)}</p>
    </DetailSection>
  ) : null;

  const phases =
    r?.phases && r.phases.length > 0 ? (
      <ol className="flex flex-col divide-y divide-border-subtle rounded-card border border-border">
          {r.phases.map((p, i) => {
            // A phase no agent has entered yet is not started, not complete.
            const frac = p.agents ? p.done / p.agents : 0;
            const current = r.status === 'running' && r.phases?.findIndex((x) => x.done < x.agents) === i;
            const empty = p.agents === 0 ? (r.status === 'running' ? 'Not started' : 'Skipped') : null;
            return (
              <li key={`${p.title}-${i}`} className="flex h-row items-center gap-3 px-3">
                <span className="w-4 shrink-0 text-caption text-fg-subtle num">{i + 1}</span>
                <span className="min-w-0 flex-1 truncate text-ui text-fg">{p.title}</span>
                {/* Not started: no empty bar, the words say it. */}
                {empty ? null : (
                  <Gauge
                    value={frac}
                    tone={r.status === 'failed' && frac < 1 ? 'failed' : current ? 'working' : 'neutral'}
                    label={`${p.title} progress`}
                    showValue={false}
                    className="w-24 shrink-0"
                  />
                )}
                <span className="w-20 shrink-0 text-right text-caption text-fg-muted num">{empty ?? `${p.done} of ${p.agents}`}</span>
              </li>
            );
          })}
        </ol>
    ) : null;

  return (
    <Drawer
      open={r != null}
      onOpenChange={(v) => !v && onClose()}
      onCloseAutoFocus={onCloseAutoFocus}
      width={480}
      layout="rows"
      eyebrow={
        r && (
          <>
            <span className="text-caption text-fg-subtle">{kindText}</span>
            <StatusPill status={PILL[r.status].status} label={PILL[r.status].label} />
          </>
        )
      }
      title={r ? (flow ? r.label : r.title) : 'Run'}
      actions={r ? <PresenceAvatars view={`flow:${r.id}`} /> : undefined}
      footer={
        session ? (
          <>
            {r?.kind === 'workflow' && (
              <Button
                variant="secondary"
                icon={<Spline />}
                className="ml-auto"
                onClick={() => {
                  onClose();
                  navigate(`/flows?run=${encodeURIComponent(r.id)}`);
                }}
              >
                Open on canvas
              </Button>
            )}
            <Button
              variant="primary"
              icon={<SquareTerminal />}
              className={r?.kind === 'workflow' ? undefined : 'ml-auto'}
              onClick={() => {
                onClose();
                navigate(`/sessions?session=${encodeURIComponent(session.id)}`);
              }}
            >
              Open session
            </Button>
          </>
        ) : undefined
      }
    >
      {r && (
        <div className="flex flex-col gap-6 px-6 py-4">
          {/* Only the eyebrow and the title stay pinned: the facts scroll with the body, so nothing draws under the header. */}
          <div className="flex flex-col gap-2">
            <FactGrid label="Run facts" lines={[atRest]} />
            {!session && <span className="text-caption text-fg-subtle">Its session is no longer reported</span>}
          </div>

          {/* A subagent leads with what it said last; a workflow with its agents. */}
          {!flow && last}
          {!r.agents?.length && <ActivityLog run={r} now={now} machineId={session?.machineId ?? null} />}
          {r.agents?.length ? <AgentList run={r} now={now} focusId={focusAgentId} machineId={session?.machineId ?? null} /> : null}
          {/*
            With the agents listed (their phases are its group headers), the phase list is
            secondary and folds; its aside counts phases, so it never restates the Finished fact
            in other numbers. With no agents listed it is the progress, open, in per cent.
          */}
          {phases &&
            (r.agents?.length ? (
              <Fold title="Phases" aside={plural(r.phases!.length, 'phase')}>
                {phases}
              </Fold>
            ) : (
              <DetailSection title="Phases" aside={`${Math.round(workflowProgress(r.phases!) * 100)}% done`}>
                {phases}
              </DetailSection>
            ))}
          {flow && last}
          {details.length > 0 && (
            <Fold title="Details">
              <FactGrid label="Run details" lines={details} />
            </Fold>
          )}
        </div>
      )}
    </Drawer>
  );
}
